import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { WeekStart } from '@/i18n/weekStart';

interface CustomDateRangePickerProps {
    startDate: Date | null;
    endDate: Date | null;
    bucket: 'day' | 'week' | 'month';
    onChange: (start: Date, end: Date, bucket: 'day' | 'week' | 'month') => void;
    /** 週の開始（将来のカレンダー整合用。現UIでは未使用） */
    weekStartsOn?: WeekStart;
}

export const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({
    startDate,
    endDate,
    bucket,
    onChange,
}) => {
    const { t } = useTranslation('search');
    const [isOpen, setIsOpen] = useState(false);
    const [tempBucket, setTempBucket] = useState<'day' | 'week' | 'month'>(bucket);
    const containerRef = useRef<HTMLDivElement>(null);

    // 外部クリックで閉じる
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // 開くときに現在の状態をセット
    const handleOpen = () => {
        setTempBucket(bucket);
        setIsOpen(true);
    };

    const handleApply = () => {
        // ※ UIモックのため、日付はそのまま（本来はここで選択された日付を渡す）
        if (startDate && endDate) {
            onChange(startDate, endDate, tempBucket);
        }
        setIsOpen(false);
    };

    const formatDateDisplay = (date: Date | null) => {
        if (!date) return '';
        return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
    };

    // モック用のショートカットリスト
    const getShortcuts = () => {
        if (tempBucket === 'day') {
            return ['今日', '昨日', '過去7日間', '過去30日間', '今月', '先月'];
        }
        if (tempBucket === 'week') {
            return ['今週', '先週', '過去4週間', '過去12週間'];
        }
        return ['今月', '先月', '過去6ヶ月', '過去1年', '今年'];
    };

    return (
        <div className="relative w-full max-w-[260px]" ref={containerRef}>
            {/* トリガーボタン */}
            <button
                type="button"
                onClick={handleOpen}
                className="w-full bg-white border-2 border-lightest-gray rounded-lg py-2 px-3 text-sm text-left flex justify-between items-center focus:outline-none focus:border-gray-400"
            >
                <span className="truncate">
                    {startDate && endDate ? `${formatDateDisplay(startDate)} ~ ${formatDateDisplay(endDate)}` : '期間を選択'}
                </span>
                <svg className="w-4 h-4 text-gray-400 shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            </button>

            {/* ポップオーバー */}
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:absolute sm:inset-auto sm:top-full sm:left-0 sm:mt-2">
                    {/* モバイル用背景オーバーレイ */}
                    <div className="fixed inset-0 bg-black/30 sm:hidden" onClick={() => setIsOpen(false)} />

                    {/* モーダルコンテンツ */}
                    <div className="relative bg-white w-full sm:w-[600px] h-[80vh] sm:h-auto sm:max-h-[500px] rounded-t-2xl sm:rounded-xl shadow-2xl flex flex-col overflow-hidden animate-slide-up sm:animate-none">
                        
                        {/* ヘッダー: 単位タブ */}
                        <div className="flex border-b border-gray-200 bg-gray-50/50">
                            {[
                                { key: 'day', label: t('bucket.day', { defaultValue: 'Day' }) },
                                { key: 'week', label: t('bucket.week', { defaultValue: 'Week' }) },
                                { key: 'month', label: t('bucket.month', { defaultValue: 'Month' }) },
                            ].map((opt) => (
                                <button
                                    key={opt.key}
                                    onClick={() => setTempBucket(opt.key as any)}
                                    className={`flex-1 py-3.5 text-sm font-medium transition-colors ${
                                        tempBucket === opt.key
                                            ? 'border-b-2 border-kotomilThemaColor text-black bg-white'
                                            : 'text-gray-500 hover:bg-gray-100'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>

                        {/* ボディ: ショートカット & カレンダー */}
                        <div className="flex flex-col sm:flex-row flex-1 min-h-0 overflow-hidden">
                            {/* ショートカット (モバイル: 横スクロール, PC: 縦リスト) */}
                            <div className="w-full sm:w-1/3 border-b sm:border-b-0 sm:border-r border-gray-100 p-3 sm:p-4 overflow-x-auto sm:overflow-y-auto flex sm:flex-col gap-2 shrink-0 bg-white">
                                {getShortcuts().map((label, i) => (
                                    <button
                                        key={i}
                                        className="px-4 py-2 text-sm text-left rounded-md hover:bg-gray-100 text-gray-700 whitespace-nowrap sm:whitespace-normal transition-colors"
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>

                            {/* カレンダーUI (モック) */}
                            <div className="flex-1 p-4 sm:p-6 overflow-y-auto bg-white flex flex-col items-center justify-center min-h-[250px]">
                                <div className="text-center space-y-4 w-full max-w-[280px]">
                                    <div className="flex justify-between items-center mb-4">
                                        <button className="p-1 hover:bg-gray-100 rounded">←</button>
                                        <span className="font-medium">2026年 4月</span>
                                        <button className="p-1 hover:bg-gray-100 rounded">→</button>
                                    </div>
                                    
                                    {/* 単位に応じたモックUIの切り替え */}
                                    {tempBucket === 'day' && (
                                        <div className="grid grid-cols-7 gap-1 text-sm">
                                            {['日', '月', '火', '水', '木', '金', '土'].map(d => (
                                                <div key={d} className="text-gray-400 py-1">{d}</div>
                                            ))}
                                            {Array.from({ length: 30 }).map((_, i) => (
                                                <div key={i} className={`py-2 rounded-full ${i >= 0 && i <= 5 ? 'bg-kotomilThemaColor/20 text-black font-medium' : 'hover:bg-gray-100'}`}>
                                                    {i + 1}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {tempBucket === 'week' && (
                                        <div className="flex flex-col gap-2 text-sm">
                                            {Array.from({ length: 5 }).map((_, i) => (
                                                <div key={i} className={`py-2 px-4 rounded-lg flex justify-between ${i === 0 ? 'bg-kotomilThemaColor/20 border border-kotomilThemaColor/50 font-medium' : 'hover:bg-gray-100 border border-transparent'}`}>
                                                    <span>第{i + 1}週</span>
                                                    <span className="text-gray-500 text-xs">4/{i*7 + 1} ~ 4/{i*7 + 7}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {tempBucket === 'month' && (
                                        <div className="grid grid-cols-3 gap-3 text-sm">
                                            {Array.from({ length: 12 }).map((_, i) => (
                                                <div key={i} className={`py-3 rounded-lg ${i === 3 ? 'bg-kotomilThemaColor/20 border border-kotomilThemaColor/50 font-medium' : 'hover:bg-gray-100 border border-transparent'}`}>
                                                    {i + 1}月
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    <p className="text-xs text-gray-400 mt-4 pt-4 border-t border-gray-100">
                                        ※ ここに {tempBucket === 'day' ? '日' : tempBucket === 'week' ? '週' : '月'} 単位の選択UIが入ります
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* フッター: アクションボタン */}
                        <div className="p-4 border-t border-gray-200 flex justify-end gap-3 bg-white shrink-0">
                            <button
                                onClick={() => setIsOpen(false)}
                                className="px-5 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={handleApply}
                                className="px-5 py-2 text-sm rounded-lg bg-kotomilThemaColor text-black hover:bg-kotomilThemaColor/90 font-medium transition-colors"
                            >
                                適用する
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
